import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { roleAPI } from '../../services/rbacApi';
import { useAuth } from '../../contexts/AuthContext';

const formatRoleLabel = (role) => {
  if (!role) return '';
  return `${role.name}${role.level ? ` (Level ${role.level})` : ''}`;
};

const SearchableRoleDropdown = ({
  value,
  onChange,
  displayLabel = '',
  disabled = false,
  error = false,
  placeholder = 'Select role',
  limit = 50,
  menuPlacement = 'down',
  excludeRoles = []
}) => {
  const { isOnlyAdmin, isSuperAdmin } = useAuth();
  const isAdminOnly = typeof isOnlyAdmin === 'function'
    ? isOnlyAdmin()
    : (typeof isSuperAdmin === 'function' ? !isSuperAdmin() : Boolean(isOnlyAdmin));

  const effectiveExcluded = useMemo(() => [
    ...excludeRoles.map(r => String(r).toUpperCase()),
    ...(isAdminOnly ? ['SUPER_ADMIN'] : [])
  ], [excludeRoles, isAdminOnly]);

  const effectiveExcludedRef = useRef(effectiveExcluded);
  useEffect(() => {
    effectiveExcludedRef.current = effectiveExcluded;
  }, [effectiveExcluded]);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [roles, setRoles] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [selectedLabel, setSelectedLabel] = useState(displayLabel);
  const [dropdownStyle, setDropdownStyle] = useState({});

  const containerRef = useRef(null);
  const dropdownUidRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const searchTimerRef = useRef(null);
  const fetchingRef = useRef(false);
  const currentSearchRef = useRef('');

  const computeDropdownStyle = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setDropdownStyle(
      menuPlacement === 'up'
        ? { position: 'fixed', bottom: window.innerHeight - rect.top + 6, left: rect.left, width: Math.max(rect.width, 260), zIndex: 9999 }
        : { position: 'fixed', top: rect.bottom + 6, left: rect.left, width: Math.max(rect.width, 260), zIndex: 9999 }
    );
  }, [menuPlacement]);

  useEffect(() => {
    setSelectedLabel(displayLabel || '');
  }, [displayLabel]);

  useEffect(() => {
    if (!value) {
      setSelectedLabel('');
      return;
    }

    if (displayLabel) {
      setSelectedLabel(displayLabel);
      return;
    }

    let isActive = true;

    const loadSelectedRole = async () => {
      try {
        const response = await roleAPI.getRole(value);
        const role = response.data?.data?.role;
        if (isActive && role) {
          setSelectedLabel(formatRoleLabel(role));
        }
      } catch (err) {
        console.error('SearchableRoleDropdown: selected role fetch error', err);
        if (isActive) {
          setSelectedLabel(String(value));
        }
      }
    };

    loadSelectedRole();

    return () => {
      isActive = false;
    };
  }, [value, displayLabel]);

  useEffect(() => () => clearTimeout(searchTimerRef.current), []);

  const fetchRoles = useCallback(async (searchTerm, page, append) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);

    try {
      const response = await roleAPI.getRoles({
        ...(searchTerm ? { search: searchTerm } : {}),
        page,
        limit,
        isActive: true
      });

      const { roles: rawRoles = [], pagination: pag = {} } = response.data?.data || {};
      const excluded = effectiveExcludedRef.current || [];
      const newRoles = excluded.length > 0
        ? rawRoles.filter((role) => !excluded.includes(String(role.code || '').toUpperCase()))
        : rawRoles;
      let mergedRoles = newRoles;

      setRoles((prev) => {
        if (!append) {
          mergedRoles = newRoles;
          return newRoles;
        }

        const existingCodes = new Set(prev.map((role) => role.code));
        mergedRoles = [...prev, ...newRoles.filter((role) => !existingCodes.has(role.code))];
        return mergedRoles;
      });
      setPagination({
        page: pag.current || pag.page || 1,
        pages: pag.pages || 1,
        total: pag.total || 0
      });
      setActiveIndex((prev) => {
        if (mergedRoles.length === 0) return -1;
        if (append && prev >= 0 && prev < mergedRoles.length) return prev;

        const selectedIndex = mergedRoles.findIndex((role) => String(role.code) === String(value));
        return selectedIndex >= 0 ? selectedIndex : 0;
      });
    } catch (err) {
      console.error('SearchableRoleDropdown: fetch error', err);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [limit]);

  const openDropdown = () => {
    if (disabled) return;
    computeDropdownStyle();
    setIsOpen(true);
    document.dispatchEvent(new CustomEvent('slim:dropdownOpened', { detail: dropdownUidRef }));
    setActiveIndex((prev) => {
      if (roles.length === 0) return prev;
      const selectedIndex = roles.findIndex((role) => String(role.code) === String(value));
      return selectedIndex >= 0 ? selectedIndex : 0;
    });

    if (roles.length === 0 && !loading) {
      currentSearchRef.current = '';
      fetchRoles('', 1, false);
    }
  };

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearch('');
    setActiveIndex(-1);
    currentSearchRef.current = '';
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const update = () => computeDropdownStyle();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);

    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen, computeDropdownStyle]);

  useEffect(() => {
    if (!isOpen || activeIndex < 0) return;

    const activeOption = listRef.current?.querySelector(`[data-role-index="${activeIndex}"]`);
    activeOption?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, isOpen]);

  const handleScroll = () => {
    const element = listRef.current;
    if (!element) return;

    const nearBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 40;
    if (nearBottom && !loading && !fetchingRef.current && pagination.page < pagination.pages) {
      fetchRoles(currentSearchRef.current, pagination.page + 1, true);
    }
  };

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        closeDropdown();
      }
    };

    const handleOtherOpen = (e) => { if (e.detail !== dropdownUidRef) closeDropdown(); };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('slim:dropdownOpened', handleOtherOpen);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('slim:dropdownOpened', handleOtherOpen);
    };
  }, [closeDropdown]);

  const handleSelect = (role) => {
    const label = formatRoleLabel(role);
    setSelectedLabel(label);
    onChange(role.code, label, role);
    closeDropdown();
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedLabel('');
    setSearch('');
    setRoles([]);
    onChange('', '', null);
    inputRef.current?.focus();
    openDropdown();
  };

  const handleInputFocus = () => {
    openDropdown();
  };

  const handleChevronMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (disabled) {
      return;
    }

    if (isOpen) {
      closeDropdown();
      inputRef.current?.blur();
      return;
    }

    openDropdown();
    inputRef.current?.focus();
  };

  const handleInputChange = (e) => {
    const nextValue = e.target.value;

    if (!isOpen) {
      openDropdown();
    }

    setSearch(nextValue);
    setActiveIndex(-1);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      currentSearchRef.current = nextValue;
      setRoles([]);
      setPagination({ page: 1, pages: 1, total: 0 });
      fetchRoles(nextValue, 1, false);
    }, 300);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) {
        openDropdown();
        return;
      }

      setActiveIndex((prev) => Math.min(prev + 1, roles.length - 1));
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!isOpen) {
        openDropdown();
        return;
      }

      setActiveIndex((prev) => Math.max(prev - 1, 0));
      return;
    }

    if (e.key === 'Enter' && isOpen) {
      e.preventDefault();
      if (activeIndex >= 0 && roles[activeIndex]) {
        handleSelect(roles[activeIndex]);
      } else if (roles.length === 1) {
        handleSelect(roles[0]);
      }
      return;
    }

    if (e.key === 'Tab') {
      closeDropdown();
      return;
    }

    if (e.key === 'Escape') {
      closeDropdown();
      inputRef.current?.blur();
    }
  };

  const hasMore = pagination.page < pagination.pages;
  const inputValue = isOpen ? search : selectedLabel;
  const inputPlaceholder = isOpen ? 'Search by role name, code, or description...' : placeholder;

  return (
    <div className="relative w-full" ref={containerRef}>
      <div
        className={`w-full rounded-lg text-sm flex items-center justify-between transition-all ${
          error
            ? 'bg-red-50 ring-2 ring-red-500'
            : 'bg-gray-50 focus-within:bg-white focus-within:ring-2 focus-within:ring-indigo-500'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onFocus={handleInputFocus}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={inputPlaceholder}
          disabled={disabled}
          className={`w-full bg-transparent border-0 p-0 text-sm focus:outline-none focus:ring-0 ${
            inputValue ? 'text-gray-900' : 'text-gray-400'
          } ${disabled ? 'cursor-not-allowed' : 'cursor-text'} px-3 py-2`}
        />
        <div className="flex items-center gap-1 flex-shrink-0 pl-2 pr-3">
          {selectedLabel && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="text-gray-300 hover:text-gray-500 leading-none text-base transition-colors"
              title="Clear selection"
            >
              ×
            </button>
          )}
          <button
            type="button"
            onMouseDown={handleChevronMouseDown}
            disabled={disabled}
            className="rounded p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:cursor-not-allowed disabled:hover:bg-transparent"
            aria-label={isOpen ? 'Close role dropdown' : 'Open role dropdown'}
          >
            <svg
              className={`w-4 h-4 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>

      {isOpen && (
        <div style={dropdownStyle} className="overflow-hidden rounded-lg border border-gray-100 bg-white shadow-xl shadow-gray-200/70 ring-1 ring-black/5">
          <div
            ref={listRef}
            onScroll={handleScroll}
            className="max-h-60 overflow-y-auto"
          >
            {pagination.total > 0 && (
              <p className="px-3 py-1.5 text-xs font-medium text-gray-500 border-b border-gray-100 bg-gray-50">
                {pagination.total} role{pagination.total !== 1 ? 's' : ''}
              </p>
            )}
            {!loading && roles.length === 0 && (
              <p className="px-3 py-5 text-sm text-gray-500 text-center">
                {search ? 'No roles match your search' : 'No roles found'}
              </p>
            )}

            {roles.map((role, index) => {
              const isSelected = String(role.code) === String(value);

              return (
                <button
                  data-role-index={index}
                  key={role._id || role.code}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelect(role);
                  }}
                  onMouseEnter={() => setActiveIndex(index)}
                  className={`w-full flex flex-col px-3 py-2 text-left transition-colors border-l-2 ${
                    activeIndex === index
                      ? 'border-indigo-500 bg-indigo-50/80 text-indigo-700'
                      : isSelected
                      ? 'border-indigo-500 bg-indigo-50/80 text-indigo-700'
                      : 'border-transparent text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-sm font-medium leading-tight">{formatRoleLabel(role)}</span>
                  <span className="text-xs text-gray-400 leading-tight truncate mt-0.5">
                    {role.code}
                    {role.description ? ` · ${role.description}` : ''}
                  </span>
                </button>
              );
            })}

            {loading && (
              <div className="flex items-center justify-center py-4 bg-white/95">
                <svg className="animate-spin w-4 h-4 text-indigo-600 mr-2" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                <span className="text-sm text-gray-500">Loading...</span>
              </div>
            )}

            {!loading && hasMore && (
              <p className="text-center text-xs text-gray-400 py-2.5 border-t border-gray-100 bg-gray-50/70">
                Scroll for more results
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableRoleDropdown;